import {
  Avatar,
  Banner,
  FieldWrapper,
  FlexBox,
  FormField,
  InputText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  SelectionList,
  Text,
} from '@dixa/lib-shared-habla';
import { colors } from '@dixa/lib-shared-tokens';
import { isResponseWithData } from 'lib-shared-auth';
import { ampli } from 'lib-shared-tracking';
import noop from 'lodash/noop';
import { useMemo } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useEffectOnce } from 'react-use';

import { authApi } from 'apis/auth';
import { chatbotsApi, ThirdPartyChatbotType } from 'apis/chatbots';
import { AppLink } from 'components/appLink';
import { HookFormSelectionListSingle, HookFormText } from 'components/hookForm';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { createAppModal } from 'utils/modal';
import { useCopy } from 'utils/useCopy';

import { isThirdPartyChatbot } from '../../utils/thirdPartyChatbotGuard';

import styles from './chatbotConnectModal.module.scss';

const formId = 'chatbot-connect';

const chatbotTypes: Record<ThirdPartyChatbotType, string> = {
  ada: 'Ada',
  digitalgenius: 'DigitalGenius',
  kindly: 'Kindly',
};

type FieldValues = {
  type: ThirdPartyChatbotType | '';
  name: string;
};

export const ChatbotConnectModal = createAppModal(
  { type: 'settings:connectChatbotModal' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const flags = useFeatureFlag([
      'ada-chat-channel',
      'digitalgenius-chat-channel',
      'kindly-chat-channel',
    ]);

    const availableChatbots = useMemo(
      () =>
        Object.keys(chatbotTypes).reduce((types, key) => {
          if (
            (key === 'ada' && flags.adaChatChannel) ||
            (key === 'digitalgenius' && flags.digitalgeniusChatChannel) ||
            (key === 'kindly' && flags.kindlyChatChannel)
          ) {
            types[key] = chatbotTypes[key];
          }

          return types;
        }, {}),
      [
        flags.adaChatChannel,
        flags.digitalgeniusChatChannel,
        flags.kindlyChatChannel,
      ],
    );

    const [connectChatbot, { isLoading, data }] =
      chatbotsApi.useCreateThirdPartyChatbotMutation();

    const [readApiTokens, { tokens, isFetching: isApiTokensFetching }] =
      authApi.useLazyReadApiTokensQuery({
        selectFromResult: ({ data, ...rest }) => ({
          ...rest,
          tokens: data?.data.tokens || [],
        }),
      });

    const chatbot = modal.arg.chatbot ?? data?.data;

    const notify = useNotifications();
    const [copyToClipboard, copyState] = useCopy();

    const form = useForm<FieldValues>({
      defaultValues: {
        type: '',
        name: '',
      },
      mode: 'all',
    });
    const selectedType = form.watch('type');

    const apiToken = useMemo(() => {
      if (
        isApiTokensFetching ||
        !tokens.length ||
        !isThirdPartyChatbot(chatbot)
      ) {
        return null;
      }

      return (
        tokens.find((token) => token.id === chatbot.apiTokenId)?.value ?? null
      );
    }, [chatbot, tokens, isApiTokensFetching]);

    const onSubmit: SubmitHandler<FieldValues> = async ({ type, name }) => {
      if (!type) {
        return;
      }

      try {
        const response = await connectChatbot({ _type: type, name }).unwrap();
        if (isResponseWithData(response)) {
          readApiTokens();
        }
        ampli.chatbotCreated({ chatbotType: response.data._type });
      } catch (error) {
        notify(
          {
            title: 'Error while creating chatbot.',
            text: 'Something went wrong.',
            icon: 'exclamation',
            className: 'danger',
            time: 5000,
          },
          NotificationType.Error,
        );
      }
    };

    const copyToClipboardAndHide = () => {
      copyToClipboard(apiToken ?? '');
      hide();
    };

    useEffectOnce(() => {
      if (chatbot && !isApiTokensFetching) {
        form.setValue('type', chatbot._type as ThirdPartyChatbotType);
        form.setValue('name', chatbot.name ?? '');
        readApiTokens(undefined, true);
      }
    });

    return (
      <Modal isOpen={isOpen} onClose={hide} className={styles.modal}>
        <ModalHead title="Connect third party chatbot" onClose={hide} />
        <ModalBody>
          <form
            id={formId}
            noValidate
            tabIndex={-1}
            onSubmit={form.handleSubmit(onSubmit)}
          >
            <FormField>
              <HookFormSelectionListSingle
                name="type"
                label="Chatbot type"
                control={form.control}
                disabled={!!chatbot}
                rules={{
                  required: 'Type is required',
                }}
                componentProps={{
                  placeholder: 'Select chatbot type',
                  getItemTitleByValue: (value) => {
                    const key = Object.keys(chatbotTypes).find(
                      (type) => type === value,
                    );

                    return key ? chatbotTypes[key] : value;
                  },
                }}
              >
                {Object.keys(availableChatbots).map((type) => (
                  <SelectionList.Item key={type} value={type}>
                    <FlexBox gap="200" alignItems="center">
                      <Avatar
                        icon={type as ThirdPartyChatbotType}
                        color={{
                          contentColor: colors.neutral[700],
                          backgroundColor: colors.neutral[100],
                        }}
                        size="24"
                      />
                      <Text.Paragraph>{chatbotTypes[type]}</Text.Paragraph>
                    </FlexBox>
                  </SelectionList.Item>
                ))}
              </HookFormSelectionListSingle>
            </FormField>
            {selectedType && (
              <>
                <Text.Paragraph variant="2" withMarginBottom>
                  Provide a name to identify your chatbot.
                </Text.Paragraph>
                <FormField>
                  <HookFormText
                    name="name"
                    label={`${chatbotTypes[selectedType]} chatbot name`}
                    disabled={!!chatbot}
                    control={form.control}
                    rules={{
                      required: 'Name is required',
                    }}
                  />
                </FormField>
              </>
            )}
          </form>
          {apiToken && (
            <>
              <Text.Paragraph variant="2" withMarginBottom>
                Copy the chatbot token and paste it in the settings of your
                chatbot to connect both systems.
              </Text.Paragraph>
              <FieldWrapper
                label={`${chatbotTypes[selectedType]} chatbot token`}
              >
                <InputText
                  readOnly
                  value={apiToken}
                  suffix={{
                    type: 'button',
                    button: {
                      icon: copyState.status === 'copied' ? 'done' : 'copy',
                      onClick: () => copyToClipboard(apiToken),
                    },
                  }}
                />
              </FieldWrapper>
            </>
          )}
          {selectedType && !chatbot && !apiToken && (
            <Banner
              className={styles.banner}
              variant="announcement"
              headline="Note about API token"
              description={
                <>
                  <Text.Paragraph>
                    This integration with {chatbotTypes[selectedType]} chatbot
                    creates an{' '}
                    <AppLink
                      path="/settings/integrations/api-tokens"
                      target="_blank"
                    >
                      API token
                    </AppLink>{' '}
                    that grants full Dixa access.{' '}
                    {selectedType === 'ada' ? (
                      <>
                        Please{' '}
                        <Text.ExternalLink
                          href="https://docs.ada.cx/docs/scripted/integrate-ada-with-other-tools/"
                          target="_blank"
                          rel="noreferrer"
                        >
                          refer to Ada
                        </Text.ExternalLink>{' '}
                        about a proper and secure usage of this token.
                      </>
                    ) : null}
                  </Text.Paragraph>
                </>
              }
            />
          )}
          {/* Display an error banner in case if unable to find an API token */}
          {chatbot && !apiToken && !isApiTokensFetching && (
            <Banner
              variant="fail"
              headline="Failed to find a token"
              description={
                <>
                  Unable to find an API token for this chatbot. It might be
                  deleted. If the error persist, try creating a new Chatbot.
                </>
              }
            />
          )}
        </ModalBody>

        <ModalFoot
          cancelName="Cancel"
          cancelOnClick={hide}
          confirmName={apiToken ? 'Copy token and close' : 'Create chatbot'}
          confirmOnClick={apiToken ? copyToClipboardAndHide : noop}
          confirmProps={{
            isLoading,
            loadingIndicator: 'Creating...',
            type: apiToken ? 'button' : 'submit',
            form: formId,
            icon: apiToken ? 'copy' : undefined,
            disabled:
              !apiToken &&
              (!form.formState.isValid ||
                form.formState.isSubmitting ||
                !form.formState.isDirty ||
                isApiTokensFetching),
          }}
        />
      </Modal>
    );
  },
);
